import { dayOffsetToEpochMs } from '../shared'
import { demoUsersByUuid } from '../tenants/tenant'
import { demoProject } from './project'

// デモのコメントデータ。グリッドのコメント列の件数と、ディスカッション
// パネルの中身になる。
//
// 投稿日はWBSの予定日と同じくday offsetで持つ。実日付を直接書くと、
// 「今日」を基準に日付をマッピングするデモの方針（shared.ts）と矛盾し、
// 時間が経つとコメントだけが過去に取り残される。

const commentAuthor = (userUuid: string) => {
  const user = demoUsersByUuid[userUuid]
  return {
    uuid: user.uuid,
    name: user.name,
    iconUrl: user.iconUrl,
  }
}

type DemoCommentSeed = {
  // wbs-items.tsのcodeと同じ値。
  wbsItemUuid: string
  text: string
  userUuid: string
  dayOffset: number
  hourOfDay: number
}

// 全項目に付けると件数バッジが画面中に散らかるため、実プロジェクト同様
// 「議論が起きた項目にだけ付いている」状態にする。
const DEMO_COMMENT_SEEDS: DemoCommentSeed[] = [
  {
    wbsItemUuid: 'FLEQ1VSA',
    text: '第1回のセッション、先方の情報システム部からも参加いただけることになりました。議題に既存システムの棚卸しを追加しています。',
    userUuid: 'demo-user-2',
    dayOffset: 5,
    hourOfDay: 10,
  },
  {
    wbsItemUuid: 'FLEQ1VSA',
    text: '承知しました。棚卸しの観点は事前に一覧をお送りしておきます。',
    userUuid: 'demo-user-3',
    dayOffset: 5,
    hourOfDay: 14,
  },
  {
    wbsItemUuid: 'FLEQ1VSA',
    text: '議事録を共有しました。次回までの宿題は先方3件・当方2件です。',
    userUuid: 'demo-user-2',
    dayOffset: 8,
    hourOfDay: 18,
  },
  {
    wbsItemUuid: 'OQJRZWE1',
    text: '受注から出荷までの分岐が想定より多く、初版のフローが1枚に収まりませんでした。通常受注と予約受注で分けて作成します。',
    userUuid: 'demo-user-4',
    dayOffset: 22,
    hourOfDay: 11,
  },
  {
    wbsItemUuid: 'OQJRZWE1',
    text: '分割の方針で問題ありません。レビューは2枚まとめて実施しましょう。',
    userUuid: 'demo-user-5',
    dayOffset: 23,
    hourOfDay: 9,
  },
  {
    wbsItemUuid: 'B6GMS5HN',
    text: '非機能要件のうち、性能目標値が未確定のままです。次回の打ち合わせで確定させたいので、現行システムの実測値を共有いただけますか。',
    userUuid: 'demo-user-3',
    dayOffset: 12,
    hourOfDay: 16,
  },
  {
    wbsItemUuid: 'B6GMS5HN',
    text: 'ピーク時のレスポンスタイムを計測して連携します。バッチの処理時間も併せて出します。',
    userUuid: 'demo-user-4',
    dayOffset: 13,
    hourOfDay: 10,
  },
  {
    wbsItemUuid: 'B6GMS5HN',
    text: '実測値を反映して更新しました。レビューをお願いします。',
    userUuid: 'demo-user-3',
    dayOffset: 17,
    hourOfDay: 15,
  },
  {
    wbsItemUuid: '5155D8TA',
    text: '業務要件定義書の初版、第4章まで作成しました。残りは来週前半で仕上げます。',
    userUuid: 'demo-user-5',
    dayOffset: 30,
    hourOfDay: 17,
  },
  {
    wbsItemUuid: '3V5WFLYA',
    text: '顧客レビューの日程が1週間後ろ倒しになりました。後続の完了報告会への影響を確認します。',
    userUuid: 'demo-user-2',
    dayOffset: 40,
    hourOfDay: 13,
  },
]

export type DemoComment = {
  uuid: string
  text: string
  group: string
  createdAt: number
  updatedAt: number
  createdBy: ReturnType<typeof commentAuthor>
  updatedBy: ReturnType<typeof commentAuthor>
  reactions: never[]
  applicationFunctionUuid?: string
  dataUuid: string
  projectUuid: string
}

// WBS項目uuid -> コメント一覧（投稿順）。実行時に追加・削除される。
export const demoCommentsByWbsItemUuid: {
  [wbsItemUuid: string]: DemoComment[]
} = DEMO_COMMENT_SEEDS.reduce(
  (acc, seed, index) => {
    const author = commentAuthor(seed.userUuid)
    const createdAt = dayOffsetToEpochMs(seed.dayOffset, seed.hourOfDay)
    const comment: DemoComment = {
      uuid: `demo-comment-${index + 1}`,
      text: seed.text,
      // 本体はdataUuid（WBS項目uuid）をそのままgroupとして扱う
      // （Comment/index.tsx）。独自の値にすると別スレッド扱いになる。
      group: seed.wbsItemUuid,
      createdAt,
      updatedAt: createdAt,
      createdBy: author,
      updatedBy: author,
      reactions: [],
      dataUuid: seed.wbsItemUuid,
      projectUuid: demoProject.uuid,
    }
    ;(acc[seed.wbsItemUuid] ??= []).push(comment)
    return acc
  },
  {} as { [wbsItemUuid: string]: DemoComment[] }
)

// コメント列が読む集計値。CommentSummary（store/comments.ts）と同じ形にする。
export const demoCommentSummary = (wbsItemUuid: string) => {
  const comments = demoCommentsByWbsItemUuid[wbsItemUuid] ?? []
  const latestComment = comments.reduce<DemoComment | undefined>(
    (latest, current) =>
      !latest || latest.createdAt < current.createdAt ? current : latest,
    undefined
  )
  return {
    groupUuid: latestComment?.group ?? '',
    hasComment: comments.length > 0,
    numberOfComments: comments.length,
    latestComment,
  }
}
